import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { assetsClipRenameClipPartialUpdate } from '@goldcast/api/content';
import { core } from '@/stores/core';
import TitlePopup from '@/components/atoms/TitlePopup';
import { useAppContext } from '@/context/AppContext/AppContext';

export default function SessionTitlePopup({ onUpdate }: { onUpdate?: () => void }) {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const initialTitle = coreStore.content?.title;
  const initialRecordingDate = coreStore.uploadData?.recording_date || '';
  const { logger } = useAppContext();

  const [title, setTitle] = useState(initialTitle);
  const [recordingDate, setRecordingDate] = useState(initialRecordingDate);

  useEffect(() => {
    if (coreStore.content?.title !== title) {
      setTitle(coreStore.content?.title);
    }
    if (!!coreStore.uploadData?.recording_date && coreStore.uploadData?.recording_date !== recordingDate) {
      setRecordingDate(coreStore.uploadData?.recording_date);
    }
  }, [coreStore.content?.title, coreStore.uploadData?.recording_date]);

  const handleChangeTitle = e => {
    setTitle(e.target.value);
  };

  const handleChangeRecordingDate = e => {
    setRecordingDate(e.target.value);
  };

  const renameContentUpload = useCallback((uploadData): Promise<any> => {
    return assetsClipRenameClipPartialUpdate({
      body: {
        title: uploadData.title!,
        upload_id: uploadData.id,
        asset_id: uploadData.full_length_asset_id
      }
    });
  }, []);

  const handleRemoteUpdate = useCallback(() => {
    const hasChanged = title !== initialTitle || recordingDate !== initialRecordingDate;

    if (!hasChanged) {
      return;
    }

    if (!title?.trim()) {
      setTitle(initialTitle);
    }

    const contentUpload = {
      ...coreStore.uploadData,
      ...coreStore.content,
      title: title?.trim(),
      recording_date: recordingDate || null
    };

    renameContentUpload(contentUpload)
      .then(() => {
        core.update(data => ({
          ...data,
          content: {
            ...data.content,
            title: contentUpload.title
          },
          uploadData: coreStore.uploadData ? contentUpload : null
        }));
        onUpdate?.();
      })
      .catch(error => {
        logger.error('Error updating session title', error);
      });
  }, [title, initialTitle, recordingDate, initialRecordingDate, coreStore.uploadData, renameContentUpload, onUpdate]);

  return (
    <TitlePopup
      title={title}
      onUpdate={handleRemoteUpdate}
      onTitleChange={handleChangeTitle}
      onRecordingDateChange={handleChangeRecordingDate}
      recordingDate={recordingDate}
    />
  );
}
